import React, { useEffect, useRef, useState } from 'react';
import './PdfPages.css';

/** Where public/pdfjs is served from — PUBLIC_URL is '' on the dev server. */
const PDFJS_ASSETS = `${process.env.PUBLIC_URL || ''}/pdfjs`;

/**
 * The worker is served VERBATIM from public/pdfjs, not referenced through
 * webpack. Referenced via `new URL(..., import.meta.url)` it still passed
 * through CRA's babel-loader, which in a production build injected
 * `import … from "/app/node_modules/@babel/runtime/…"` statements into the
 * emitted file. Those paths do not exist on the server, so the module worker
 * failed to start on dev (2026-10-06) and every PDF fell back to the iframe.
 * The development build injects no helpers, which is why it worked locally.
 *
 * Unlike webpack's assets the file name carries no content hash, and `serve`
 * sends no Cache-Control, so a browser may keep an old worker for hours after a
 * pdfjs-dist bump — and pdf.js refuses a worker whose version differs from its
 * own. `?v=` keys the cache on the version the bundle was built with.
 */
const PDF_WORKER_URL = `${PDFJS_ASSETS}/pdf.worker.min.mjs`;

/**
 * Renders every page of a PDF to canvas, stacked, at the container's width —
 * the plain "just the document" the design draws (Figma 1020-3796), where the
 * browser's built-in viewer put its own toolbar and dark backdrop around it.
 *
 * Because we own the rendering, the jump to the evidence page WORKS here, where
 * the iframe's `#page=` fragment was ignored on blob: URLs. The target page is
 * scrolled into view once it is drawn.
 *
 * pdf.js is loaded on demand — it is ~400 KB gzipped and only this step needs
 * it — and parses in a web worker served from public/pdfjs (PDF_WORKER_URL).
 *
 * Render passes are SERIALISED. pdf.js throws if render() is called on a canvas
 * that another render is still drawing into, and that is exactly what a resize
 * landing mid-draw does — the first cut of this drew blank pages on dev for
 * that reason. One pass runs at a time; a newer request cancels the running
 * one, waits for it to stop, then draws.
 */
const PdfPages = ({ url, page, fileName, onError }) => {
  // The evidence page comes off the wire; compare it as a number so "2" and 2
  // both reach the jump below (strict equality against the loop index).
  const targetPage = Number(page) || 0;
  const containerRef = useRef(null);
  const pageRefs = useRef([]);
  const docRef = useRef(null);
  const generation = useRef(0); // bumped to retire the pass in flight
  const chain = useRef(Promise.resolve()); // passes queue behind one another
  const activeTask = useRef(null);
  const lastWidth = useRef(0);
  const jumped = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [pageCount, setPageCount] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  // 1. Load the document. The loading task owns the document and the worker,
  //    so destroying it is the teardown (PDFDocumentProxy has no destroy()).
  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    setStatus('loading');
    setPageCount(0);
    docRef.current = null;
    jumped.current = false;

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        if (cancelled) return;
        pdfjs.GlobalWorkerOptions.workerSrc = `${PDF_WORKER_URL}?v=${pdfjs.version}`;
        loadingTask = pdfjs.getDocument({
          url,
          // Image decoders pdf.js fetches by URL at decode time, served from
          // public/pdfjs (see the README there). Scanned forms are routinely
          // JBIG2, and without jbig2.wasm those pages paint blank.
          // `useWorkerFetch` is stated outright: pdf.js only infers it when
          // every asset url (fonts, CMaps too) is given, and without it the
          // wasm decoders are silently disabled. Fonts and CMaps are not
          // shipped — this views scans, and text falls back to system fonts.
          useWorkerFetch: true,
          wasmUrl: `${PDFJS_ASSETS}/wasm/`,
          iccUrl: `${PDFJS_ASSETS}/iccs/`,
        });
        const doc = await loadingTask.promise;
        if (cancelled) return;
        docRef.current = doc;
        setPageCount(doc.numPages);
        setStatus('ready');
      } catch (err) {
        // Teardown destroys the loading task, which rejects with "Loading
        // aborted" — not a failure, so it is checked before anything is logged.
        if (cancelled) return;
        // The UI shows a generic line; the reason goes to the console so a
        // failing document can be diagnosed from a screenshot of DevTools.
        console.error('[PdfPages] could not load document:', err);
        setStatus('error');
        onErrorRef.current?.(err);
      }
    })();

    return () => {
      cancelled = true;
      generation.current += 1;
      try { activeTask.current?.cancel(); } catch (_) { /* already finished */ }
      docRef.current = null;
      if (loadingTask) loadingTask.destroy().catch(() => {});
    };
  }, [url]);

  // 2. Draw once the canvases exist (this effect runs after they commit), and
  //    again when the container's width changes.
  useEffect(() => {
    const doc = docRef.current;
    const container = containerRef.current;
    if (!doc || !container || pageCount === 0) return undefined;

    const drawPass = async (gen) => {
      const width = container.clientWidth;
      if (!width) return;
      lastWidth.current = width;
      const dpr = window.devicePixelRatio || 1;

      for (let n = 1; n <= doc.numPages; n += 1) {
        if (gen !== generation.current) return; // retired by a newer pass
        const canvas = pageRefs.current[n - 1];
        if (!canvas) continue;
        const pdfPage = await doc.getPage(n);
        if (gen !== generation.current) return;
        const scale = width / pdfPage.getViewport({ scale: 1 }).width;
        const viewport = pdfPage.getViewport({ scale });
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const context = canvas.getContext('2d');
        if (!context) continue;
        const task = pdfPage.render({
          canvasContext: context,
          viewport,
          transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
        });
        activeTask.current = task;
        try {
          await task.promise;
        } catch (err) {
          if (err?.name === 'RenderingCancelledException') return;
          throw err;
        } finally {
          if (activeTask.current === task) activeTask.current = null;
        }
        // Page 1 is already in view under the banner; only a later page is
        // scrolled to, and only once — not again on every resize.
        if (!jumped.current && targetPage > 1 && n === targetPage) {
          jumped.current = true;
          canvas.scrollIntoView({ block: 'start' });
        }
      }
    };

    const requestPass = () => {
      const gen = generation.current + 1;
      generation.current = gen;
      try { activeTask.current?.cancel(); } catch (_) { /* already finished */ }
      chain.current = chain.current
        .then(() => drawPass(gen))
        .catch((err) => {
          if (gen !== generation.current) return;
          console.error('[PdfPages] could not draw document:', err);
          setStatus('error');
          onErrorRef.current?.(err);
        });
    };

    requestPass();

    let observer = null;
    let resizeTimer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        // The observer also fires once on observe(); only a real change redraws.
        if (container.clientWidth === lastWidth.current) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(requestPass, 150);
      });
      observer.observe(container);
    }

    return () => {
      clearTimeout(resizeTimer);
      if (observer) observer.disconnect();
      generation.current += 1;
      try { activeTask.current?.cancel(); } catch (_) { /* already finished */ }
    };
  }, [pageCount, targetPage]);

  return (
    <div ref={containerRef} className="pdfpages" data-testid="pdf-pages" data-page={page || undefined}>
      {status === 'loading' && <p className="pdfpages-state">Loading document…</p>}
      {status === 'error' && (
        <p className="pdfpages-state pdfpages-state--error">
          Couldn’t display this document here. Use the link above to open it in a new tab.
        </p>
      )}
      {Array.from({ length: pageCount }, (_, i) => (
        <canvas
          key={i}
          ref={(el) => { pageRefs.current[i] = el; }}
          className="pdfpages-page"
          role="img"
          aria-label={`${fileName || 'Document'}, page ${i + 1} of ${pageCount}`}
        />
      ))}
    </div>
  );
};

export default PdfPages;
